import React, { useContext, useEffect } from "react";
import { Form } from "react-final-form";
import arrayMutators from "final-form-arrays";
import { IntlContext } from "context";
import { ChevronLeft, ChevronRight } from "lucide-react";
import formatErrorResponse from "helpers/form/formatErrorResponse";
import { Slider } from "components/Form/Field";
import Button from "ui/Button";
import Address from "components/Form/Address";
import { useRegisterStepper } from "../common/RegisterStepper";
import { useRegisterDraftGuard } from "../common/useRegisterDraftGuard";
import type { RegisterDraft } from "../types";

import "leaflet/dist/leaflet.css";

import { useField } from "react-final-form";
import { Circle, MapContainer, Marker, TileLayer, useMap } from "react-leaflet";
import L from "leaflet";

import marker2xUrl from "leaflet/dist/images/marker-icon-2x.png";
import markerUrl from "leaflet/dist/images/marker-icon.png";
import markerShadowUrl from "leaflet/dist/images/marker-shadow.png";
import { House, Building2, Globe } from "lucide-react";
import { getErrorResponseData } from "helpers/typing";
import { trackRegisterStepComplete } from "lib/analytics/dataLayer";

interface Props {
    provider: RegisterDraft | undefined;
    setProvider: (provider: RegisterDraft | undefined) => void;
}

const DEFAULT_CENTER: [number, number] = [47.218371, -1.553621]; // Nantes

function kmToMeters(km: number) {
    return Math.round(km * 1000);
}

function toFiniteNumber(value: unknown): number | null {
    if (typeof value === "number" && Number.isFinite(value)) return value;
    if (typeof value === "string" && value.trim() !== "") {
        const n = Number(value);
        return Number.isFinite(n) ? n : null;
    }
    return null;
}

const MapAutoFit: React.FC<{
    center: [number, number];
    radiusMeters: number;
    enabled: boolean;
}> = ({ center, radiusMeters, enabled }) => {
    const map = useMap();

    useEffect(() => {
        if (!enabled) return;

        let cancelled = false;

        // Picking a city triggers layout + map updates; fitting too early can crash Leaflet
        // with `layerPointToLatLng` undefined. Wait for the map to be ready and for the
        // container to have a stable size before fitting.
        map.whenReady(() => {
            if (cancelled) return;

            requestAnimationFrame(() => {
                if (cancelled) return;

                try {
                    map.invalidateSize();
                    // Ensure we always pan to the selected city first.
                    map.setView(center, map.getZoom(), { animate: false });
                    const bounds = L.circle(center, { radius: radiusMeters }).getBounds();
                    map.fitBounds(bounds, { padding: [18, 18], animate: false });
                } catch {
                    // If Leaflet is in an unstable state (rare), skip this cycle.
                }
            });
        });

        return () => {
            cancelled = true;
        };
    }, [center, enabled, map, radiusMeters]);

    return null;
};

const CityMapPreview: React.FC = () => {
    const latRaw = useField<string | number | null | undefined>("coordinates_lat", {
        subscription: { value: true },
    }).input.value;
    const lonRaw = useField<string | number | null | undefined>("coordinates_lon", {
        subscription: { value: true },
    }).input.value;
    const travelRange = useField<number | null>("travel_range", { subscription: { value: true } }).input.value;

    const lat = toFiniteNumber(latRaw);
    const lon = toFiniteNumber(lonRaw);

    const center: [number, number] =
        typeof lat === "number" && typeof lon === "number" ? [lat, lon] : DEFAULT_CENTER;

    const radiusMeters = kmToMeters(typeof travelRange === "number" ? travelRange : 10);

    const hasSelectedCity = typeof lat === "number" && typeof lon === "number";

    const markerIcon = React.useMemo(
        () =>
            L.icon({
                iconUrl: markerUrl,
                iconRetinaUrl: marker2xUrl,
                shadowUrl: markerShadowUrl,
                iconSize: [25, 41],
                iconAnchor: [12, 36],
                shadowSize: [41, 41],
            }),
        []
    );

    return (
        <div className="relative aspect-square w-full overflow-hidden rounded-[28px] border bg-muted">
            <MapContainer center={center} zoom={10} className="h-full w-full">
                <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <MapAutoFit center={center} radiusMeters={radiusMeters} enabled={hasSelectedCity} />
                <Circle center={center} radius={radiusMeters} pathOptions={{ color: "#4f46e5", fillOpacity: 0.15 }} />
                <Marker position={center} icon={markerIcon} />
            </MapContainer>

            <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-background/10" />
        </div>
    );
};

const RegisterProviderArea: React.FC<Props> = ({ provider, setProvider }) => {
    const intl = useContext(IntlContext);
    const stepper = useRegisterStepper();
    useRegisterDraftGuard(provider, "prestations");

    const onSubmit = async (values: RegisterDraft) => {
        try {
            setProvider(Object.assign({}, provider, values));
            trackRegisterStepComplete({
                registerType: "provider",
                stepKey: "intervention",
                stepIndex: 2,
                stepLabel: intl.formatMessage("authentication.register.provider_flow.area.label"),
            });
            stepper.goTo("informations");
        } catch (err: unknown) {
            const errData = getErrorResponseData(err);
            if (errData) {
                return formatErrorResponse(errData);
            }
            throw err;
        }
    };

    return (
        <React.Fragment>
            <Form
                onSubmit={onSubmit}
                initialValues={{
                    ...provider,
                    travel_range: provider?.travel_range ?? 10,
                }}
                mutators={{ ...arrayMutators }}
                render={({ handleSubmit, submitting, form, values }) => (
                    <form onSubmit={handleSubmit} className="register__form">
                        <div className="flex flex--direction-column gap-10">
                            <p className="h1 font-black">
                                Où souhaitez-vous <span className="block text-primary">intervenir ?</span>
                            </p>
                            <p className="h4 margin-bottom--2 margin-top--1">
                                Définissez votre zone d'intervention pour recevoir des demandes près de chez vous.
                            </p>

                            <div className="margin-top--6 space-y-6">
                                <div className="space-y-2">
                                    <div className="text-xs font-bold uppercase tracking-wider">
                                        Votre adresse postale
                                    </div>
                                    <Address
                                        inputProps={{
                                            name: "address",
                                            latitudeName: "coordinates_lat",
                                            longitudeName: "coordinates_lon",
                                            houseNumber: "house_number",
                                            street: "street",
                                            postcode: "postcode",
                                            city: "city",
                                            commune_code: "commune_code",
                                            required: true,
                                            placeholder: "ex: 123 Rue de la Paix, 75000 Paris",
                                        }}
                                    />
                                </div>

                                <div className="grid gap-6 lg:grid-cols-2">
                                    <div className="max-w-[340px]">
                                        <CityMapPreview />
                                    </div>

                                    <div className="space-y-5">
                                        <div className="flex items-center justify-between">
                                            <div className="text-xs font-bold uppercase tracking-wider">
                                                Rayon d'intervention
                                            </div>
                                            <div className="text-xl font-bold text-foreground">
                                                {(toFiniteNumber(form.getFieldState("travel_range")?.value) ?? 10).toString()} km
                                            </div>
                                        </div>

                                        <Slider
                                            parse={(value) => {
                                                const n = toFiniteNumber(value);
                                                return typeof n === "number" ? n : value;
                                            }}
                                            inputProps={{
                                                name: "travel_range",
                                                min: 5,
                                                max: 50,
                                                step: 1,
                                                unit: "km",
                                                required: true,
                                            }}
                                        />

                                        <div className="space-y-2">
                                            <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                                                Sélection rapide
                                            </div>
                                            <div className="grid grid-cols-3 gap-3">
                                                {[5, 10, 20].map((v) => {
                                                    const selected = (form.getFieldState("travel_range")?.value ?? 10) === v;
                                                    return (
                                                        <button
                                                            key={v}
                                                            type="button"
                                                            className={
                                                                "rounded-2xl border px-4 py-4 text-left transition " +
                                                                (selected
                                                                    ? "border-primary bg-primary/5 text-primary"
                                                                    : "bg-background hover:bg-accent/40")
                                                            }
                                                            onClick={() => form.change("travel_range", v)}
                                                        >
                                                            <div className="text-lg font-semibold">
                                                                {v === 5 ? <House className="h-4 w-4 text-muted-foreground" /> : v === 10 ? <Building2 className="h-4 w-4 text-muted-foreground" /> : <Globe className="h-4 w-4 text-muted-foreground" />}
                                                            </div>
                                                            <div className="text-lg font-bold py-1">{v} km</div>
                                                            <div className="text-xs text-muted-foreground">
                                                                {v === 5 ? "Hyperlocal" : v === 10 ? "Quartiers" : "Agglo"}
                                                            </div>
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        
                            <div className="flex items-center justify-between text-center margin-top--10">
                                <Button
                                    className="btn inline-flex items-center justify-center gap-3 rounded-full px-0 py-4 font-black text-muted-foreground/70"
                                    onClick={() => {
                                        setProvider(Object.assign({}, provider, values));
                                        stepper.goTo("prestations");
                                    }}
                                >
                                    <ChevronLeft className="h-4 w-4" />
                                    {intl.formatMessage("generic.step.previous")}
                                </Button>
                                <Button
                                    type="submit"
                                    className="btn btn--primary inline-flex items-center justify-center gap-3 rounded-full font-black px-10 py-4"
                                    disabled={submitting}
                                >
                                    {intl.formatMessage("generic.step.next")}
                                    <ChevronRight className="h-4 w-4" />
                                </Button>
                            </div>
                        </div>
                    </form>
                )}
            />
        </React.Fragment>
    );
};

export default RegisterProviderArea;
